import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, Modal } from 'antd';

import {
  selectApplications,
  selectDashboardApplications
} from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';
import { updateApplication } from 'features/applications/applicationsSlice';

import { useAppDispatch } from 'hooks/useTypedRedux';

import { notify } from 'utils/toast/createToast';

import { ClientApplicationStages } from 'constants/enums';

import styles from './ApplicationRejectModal.module.scss';

const APPLICATION_REJECT_MODEL_WIDTH = 550;

interface ApplicationRejectModalProps {
  applicationId?: string;
  onClose: () => void;
}

const ApplicationRejectModal: FC<ApplicationRejectModalProps> = ({
  applicationId,
  onClose
}: ApplicationRejectModalProps) => {
  const { t } = useTranslation();
  const applications = useSelector(selectApplications);
  const dashboardApplications = useSelector(selectDashboardApplications);
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const application =
    applications?.find((el) => el.id === applicationId) ||
    dashboardApplications?.find((el) => el.id === applicationId);

  const handleDeclineApplication = async () => {
    if (!applicationId) {
      return;
    }

    try {
      setIsSaving(true);

      await applicationsService.changeApplicationStage(
        applicationId,
        ClientApplicationStages.DeclinedApplicationStageClient
      );
      dispatch(
        updateApplication({
          id: applicationId,
          clientStage: ClientApplicationStages.DeclinedApplicationStageClient
        })
      );

      onClose();
    } catch (error) {
      notify({
        description: t('error.message.rejectApplication')
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      centered
      width={APPLICATION_REJECT_MODEL_WIDTH}
    >
      <div className={styles.wrap}>
        <div className={styles.title}>
          {t('modal.applicationReject.title', {
            name: application?.jobseekerFullName || ''
          })}
        </div>
        <div className={styles.buttons}>
          <Button danger onClick={onClose} disabled={isSaving}>
            {t('modal.applicationReject.cancelButton')}
          </Button>
          <Button
            danger
            className={styles.applyButton}
            onClick={handleDeclineApplication}
            disabled={isSaving}
            loading={isSaving}
          >
            {t('modal.applicationReject.applyButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ApplicationRejectModal;
